/**
 * El recibo, escrito en el idioma de la impresora térmica.
 *
 * Este archivo traduce una venta a comandos ESC/POS. Es el equivalente exacto de
 * lo que `Recibo.tsx` dibuja en pantalla — y eso es a propósito: **el papel y la
 * pantalla tienen que decir lo mismo**. Si algún día se agrega una línea al
 * recibo, hay que agregarla en los dos sitios.
 *
 * ⚠️ Igual que en pantalla, aquí NO se recalcula nada. Los importes se copian tal
 * cual del documento que devolvió el servidor. Un recibo es un documento
 * histórico: reimprimirlo dentro de dos años tiene que dar el mismo papel.
 */

import { Ticket, recortar, type AnchoPapel, type PaginaDeCodigos } from "@/lib/escpos";
import type { ImagenDeTicket } from "@/lib/imagenTicket";
import { formatMoney, formatRate, toNumber, type FormatoMoneda, monedaImpresa } from "@/lib/money";
import { reciboComoPdf } from "@/lib/reciboPdf";
import { TicketEnLineas } from "@/lib/ticketLineas";
import { conImpuestosDeLaVenta, llevaMarcaDeExento, nombresDeLosExentos } from "@/lib/exentos";
import type { Sale, Tenant } from "@/lib/types";

/** Lo que necesita saber el ticket sobre cómo está configurada la impresora. */
export type OpcionesTicket = {
  ancho: AnchoPapel;
  pagina: PaginaDeCodigos;
  cortarPapel: boolean;
  abrirCajon: boolean;
  /** Para dar formato correcto a cada moneda (los pesos no llevan decimales). */
  monedas?: FormatoMoneda[];

  /**
   * El logo, ya convertido a puntos por `cargarImagenParaTicket`.
   *
   * ⚠️ Llega convertido y no como una dirección: este archivo se prueba sin
   * navegador, y cargar una imagen necesita un `<canvas>`. Quien imprime lo
   * carga antes y lo pasa hecho.
   *
   * Si falta, el recibo sale sin logo y ya. Un recibo sin logo se entiende
   * igual; uno que no sale por culpa del logo deja al cliente sin comprobante.
   */
  logo?: ImagenDeTicket | null;

  /**
   * En qué moneda se imprimen los importes, si no es la de la venta: el
   * código y la tasa del día de la venta (1 de la principal = `tasa`).
   * `null` o ausente = en la moneda de la venta, como siempre.
   */
  moneda?: { codigo: string; tasa: number } | null;

  /**
   * Qué código va debajo de cada producto (2026-10-07): el SKU, el código de
   * barras o ninguno. Sin elegir, ninguno: el rollo de siempre.
   */
  codigo?: "sku" | "barcode" | "ninguno" | null;
};

/** Formato por defecto cuando no conocemos la moneda: dos decimales y sin símbolo. */
const GENERICA: FormatoMoneda = { code: "", symbol: "", decimals: 2, symbol_before: false };

function formatoDe(codigo: string, monedas: FormatoMoneda[] | undefined): FormatoMoneda {
  return monedas?.find((m) => m.code === codigo) ?? GENERICA;
}

/** "12,50 USD" — sin símbolo, porque en 32 columnas el símbolo y el código juntos sobran. */
function monto(valor: string, codigo: string, monedas: FormatoMoneda[] | undefined): string {
  return `${formatMoney(valor, formatoDe(codigo, monedas), { sinSimbolo: true })} ${monedaImpresa(codigo)}`;
}

/** Solo el número, para la columna de importes donde la moneda ya se sobreentiende. */
function cifra(valor: string, codigo: string, monedas: FormatoMoneda[] | undefined): string {
  return formatMoney(valor, formatoDe(codigo, monedas), { sinSimbolo: true });
}

/** "2" en vez de "2.000", "1,5" en vez de "1.500". */
function cantidad(qty: string): string {
  const n = toNumber(qty);
  return Number.isInteger(n) ? String(n) : n.toLocaleString("es-VE", { maximumFractionDigits: 3 });
}

function fechaHora(iso: string): string {
  return new Date(iso).toLocaleString("es-VE", { dateStyle: "short", timeStyle: "short" });
}

// =============================================================================
//  El ticket de una venta
// =============================================================================

/**
 * Lo que `componerTicket` necesita saber hacer.
 *
 * ⚠️ EXISTE PARA QUE HAYA UN SOLO RECIBO, NO DOS.
 *
 * El mismo recibo sale por dos caminos: bytes para la impresora térmica
 * (`Ticket`) y líneas de texto para el PDF (`TicketEnLineas`). Si cada uno
 * tuviera su propia función de composición, el día que se añada una línea
 * —un impuesto nuevo, el nombre del vendedor— se añadiría en uno y no en el
 * otro, y nadie se enteraría hasta que un cliente comparase dos papeles.
 *
 * Con esto, `componerTicket` no sabe con quién habla: escribe el recibo una
 * vez y quien lo recibe decide en qué acaba.
 */
export interface ArmadorDeTicket {
  /*
    ⚠️ Todos devuelven el propio armador, no `void`.

    El recibo se escribe encadenando —`t.centrar().negrita().doble()`— y con
    `void` o `unknown` la segunda llamada ya no compila. La interfaz tiene que
    prometer lo mismo que prometen las dos clases de verdad.
  */
  readonly anchoLinea: number;
  iniciar(): ArmadorDeTicket;
  izquierda(): ArmadorDeTicket;
  centrar(): ArmadorDeTicket;
  derecha(): ArmadorDeTicket;
  negrita(activa?: boolean): ArmadorDeTicket;
  doble(): ArmadorDeTicket;
  normal(): ArmadorDeTicket;
  linea(t?: string): ArmadorDeTicket;
  salto(cantidad?: number): ArmadorDeTicket;
  columnas(izquierda: string, derecha: string, relleno?: string): ArmadorDeTicket;
  parrafo(t: string): ArmadorDeTicket;
  separador(caracter?: string): ArmadorDeTicket;
  avanzar(lineas?: number): ArmadorDeTicket;
  cortar(parcial?: boolean): ArmadorDeTicket;
  abrirCajon(pin?: 2 | 5): ArmadorDeTicket;
  imagen(anchoEnPuntos: number, alto: number, bits: Uint8Array): ArmadorDeTicket;
}

/**
 * El mismo armador, pero con TODO en negrita.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LO PIDIÓ EL DUEÑO CON EL PAPEL EN LA MANO (2026-09-25)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El papel térmico sale tenue, y con solo los nombres en negrita el resto del
 * recibo se leía gris: la fecha, los productos, la tasa. Todo en negrita se lee
 * de lejos y resiste mejor a que el papel se desvanezca.
 *
 * Se hace envolviendo el armador y no tocando cada línea: los «quitar negrita»
 * se ignoran y cada «volver a normal» la vuelve a poner. Así vale igual para
 * el recibo, la cotización y el ticket de prueba, y lo que se añada mañana
 * sale en negrita sin acordarse.
 *
 * ⚠️ Solo el TICKET (la térmica y su PDF del ancho del rollo). La hoja carta va
 * por otro camino y se queda como está: ahí la tinta no se desvanece.
 */
export function enNegrita(t: ArmadorDeTicket): ArmadorDeTicket {
  const envuelto: ArmadorDeTicket = {
    get anchoLinea() {
      return t.anchoLinea;
    },
    iniciar: () => (t.iniciar().negrita(true), envuelto),
    izquierda: () => (t.izquierda(), envuelto),
    centrar: () => (t.centrar(), envuelto),
    derecha: () => (t.derecha(), envuelto),
    negrita: () => (t.negrita(true), envuelto),
    doble: () => (t.doble(), envuelto),
    normal: () => (t.normal().negrita(true), envuelto),
    linea: (texto?: string) => (t.linea(texto), envuelto),
    salto: (cantidad?: number) => (t.salto(cantidad), envuelto),
    columnas: (izquierda: string, derecha: string, relleno?: string) => (
      t.columnas(izquierda, derecha, relleno), envuelto
    ),
    parrafo: (texto: string) => (t.parrafo(texto), envuelto),
    separador: (caracter?: string) => (t.separador(caracter), envuelto),
    avanzar: (lineas?: number) => (t.avanzar(lineas), envuelto),
    cortar: (parcial?: boolean) => (t.cortar(parcial), envuelto),
    abrirCajon: (pin?: 2 | 5) => (t.abrirCajon(pin), envuelto),
    imagen: (anchoEnPuntos: number, alto: number, bits: Uint8Array) => (
      t.imagen(anchoEnPuntos, alto, bits), envuelto
    ),
  };
  return envuelto;
}

/** El recibo, escrito una sola vez, sobre el armador que le den. */
/**
 * La dirección y el teléfono que van debajo del nombre del negocio, en el
 * recibo y en la cotización.
 *
 *   · La dirección es la de la sucursal donde se vendió. Si no tiene, NO se
 *     pone la del negocio: el recibo de un local con la dirección de otro
 *     confundía más que ayudaba (decisión del dueño).
 *   · El teléfono es el de la sucursal o el de la sede principal (eso ya
 *     viene resuelto del servidor); el del negocio queda de último recurso.
 *   · Y de los dos, solo lo que el negocio eligió en Funciones → «Datos de
 *     contacto en el recibo» (`recibo_contacto`). Sin elegir, los dos.
 */
export function contactoDelPapel(
  papel: Pick<Sale, "direccion_sucursal" | "telefono_sucursal">,
  negocio: Tenant | undefined,
): { direccion: string | null; telefono: string | null } {
  const eleccion = negocio?.settings?.recibo_contacto ?? "ambos";
  const conDireccion = eleccion === "ambos" || eleccion === "direccion";
  const conTelefono = eleccion === "ambos" || eleccion === "telefono";
  return {
    direccion: conDireccion ? papel.direccion_sucursal || null : null,
    telefono: conTelefono ? papel.telefono_sucursal || negocio?.phone || null : null,
  };
}

export function componerTicket(
  t: ArmadorDeTicket,
  venta: Sale,
  negocio: Tenant | undefined,
  op: OpcionesTicket,
): void {
  t = enNegrita(t);
  const mon = op.monedas;
  const ancho = t.anchoLinea;

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EL RECIBO EN OTRA MONEDA (lo pidió el dueño, 2026-09-25)
    ═══════════════════════════════════════════════════════════════════════════

    «Imprimir en bolívares o en dólares indiferentemente de si se paga en
    bolívares o en divisas.» Los productos, la cuenta y el total salen en la
    moneda elegida, convertidos con la tasa DEL DÍA DE LA VENTA —el papel de
    una venta de ayer dice lo mismo que ayer—, y bajo el total va la tasa y el
    equivalente, para que nadie tenga que preguntar.

    La forma de pago ya no va en el papel (2026-09-29): ver más abajo.
  */
  const otra =
    op.moneda && op.moneda.codigo !== venta.currency_code && op.moneda.tasa > 0 ? op.moneda : null;
  const codigoDelPapel = otra?.codigo ?? venta.currency_code;
  const convertir = (valor: string): string =>
    otra ? String(toNumber(valor) * otra.tasa) : valor;
  const importe = (valor: string): string => cifra(convertir(valor), codigoDelPapel, mon);

  t.iniciar();

  // --- El logo, si lo hay ---------------------------------------------------
  //
  // Va antes del nombre y NO en vez del nombre. Un logo se reconoce de lejos
  // pero no se lee: quien tenga que reclamar dentro de un mes necesita el
  // nombre escrito, y el papel térmico se desvanece antes que la memoria.
  if (op.logo) {
    t.centrar().imagen(op.logo.ancho, op.logo.alto, op.logo.bits).salto();
  }

  // --- Encabezado: los datos del negocio ------------------------------------
  /*
   * ⚠️ El nombre respira. Antes iba pegado a la línea de datos del negocio y
   * esa a la raya, y el papel empezaba como un bloque de texto sin cabeza. Un
   * salto cuesta tres milímetros de rollo y es lo primero que hace que el
   * recibo parezca de un negocio y no de una máquina.
   */
  t.centrar().negrita().doble();
  t.parrafo(negocio?.name ?? "SOFTFI");
  t.normal().centrar();

  if (negocio?.legal_name) t.parrafo(negocio.legal_name);
  if (negocio?.tax_id) t.linea(negocio.tax_id);
  // La dirección y el teléfono del local donde se vendió, según lo que el
  // negocio eligió imprimir. Ver `contactoDelPapel`.
  const { direccion, telefono } = contactoDelPapel(venta, negocio);
  if (direccion) t.parrafo(direccion);
  if (telefono) t.linea(`Tel: ${telefono}`);

  t.salto();
  t.izquierda().separador();

  // --- Datos de la venta ----------------------------------------------------
  /*
   * ⚠️ El número del recibo va en negrita.
   *
   * Es el dato con el que vuelve el cliente —«vengo por el recibo 41»— y el
   * único de este bloque que alguien va a tener que buscar con el papel en la
   * mano, a veces medio borrado. Los demás son contexto.
   */
  t.negrita().columnas("RECIBO", venta.number).negrita(false);
  // La corregida dice a cuál reemplaza: el cliente puede tener el papel viejo.
  if (venta.corrige_a_numero) t.columnas("Corrige a", venta.corrige_a_numero);
  t.columnas("Fecha", fechaHora(venta.created_at));
  // ⚠️ Los nombres en negrita (lo pidió el dueño): quién atendió y para quién
  // es, que es lo que se busca en el papel cuando hay un reclamo.
  if (venta.sold_by_name) {
    // «Asesor» y no «Atendió» (lo pidió el dueño, 2026-10-01).
    t.negrita().columnas("Asesor", recortar(venta.sold_by_name, ancho - 10)).negrita(false);
  }
  if (venta.customer_name) {
    t.negrita().columnas("Cliente", recortar(venta.customer_name, ancho - 10)).negrita(false);
  }
  // Y su cédula o RIF, que faltaba (lo pidió el dueño, 2026-09-25).
  if (venta.customer_documento) {
    t.columnas(
      negocio?.country_code === "VE" ? "C.I./RIF" : "Documento",
      recortar(venta.customer_documento, ancho - 11),
    );
  }

  t.separador();

  /*
   * ⚠️ Un título antes de los productos.
   *
   * Sin él, la primera línea de la lista se confunde con la última del bloque
   * de arriba: las dos son texto a la izquierda con un número a la derecha, y
   * el ojo no encuentra dónde empieza lo que compró.
   */
  t.negrita().linea("DETALLE").negrita(false);

  // --- Las líneas de la venta -----------------------------------------------
  /*
   * Cada producto ocupa dos líneas: el nombre completo arriba (así no hay que
   * recortarlo y el cliente reconoce lo que compró) y "cantidad x precio" a la
   * izquierda con el importe a la derecha, abajo.
   *
   * Con una sola línea, en 58 mm (32 columnas) no cabe casi nada: "Shampoo
   * anticaspa 400ml" ya son 26 caracteres.
   */
  // «(E)»: exento de alguno de los impuestos que se cobraron en esta venta.
  const cobrados = (venta.impuestos_de_pedido ?? []).map((x) => x.tax_id);
  const exentosDe = nombresDeLosExentos(venta.items, venta.impuestos_de_pedido ?? []);

  for (const l of venta.items) {
    const marca = llevaMarcaDeExento(l.exento_de, cobrados) ? " (E)" : "";
    /*
      El código que se eligió en la ventana del recibo (2026-10-07), SIN
      RÓTULO —ni «SKU» ni «Cód.»: el código ya dice lo que es—. Lo decidió el
      dueño con el papel delante:
        · el SKU, a la izquierda del nombre: «FIJ-TIR-02 Tirro de 2"»;
        · el código de barras, en su línea debajo del nombre, porque es largo.
    */
    const sku = op.codigo === "sku" ? l.sku : null;
    const barras = op.codigo === "barcode" ? l.barcode : null;
    const nombre =
      (sku ? `${recortar(sku, ancho - 4)} ` : "") +
      (l.variant_name ? `${l.name} (${l.variant_name})` : l.name) +
      marca;
    t.negrita().parrafo(nombre).negrita(false);
    if (barras) t.linea(`  ${recortar(barras, ancho - 2)}`);
    t.columnas(
      `  ${cantidad(l.qty)} x ${importe(l.unit_price)}`,
      importe(l.line_total),
    );
    // El precio antes y después del IVA: lo pidió el dueño para la factura.
    const conIva = conImpuestosDeLaVenta(
      toNumber(l.line_total),
      l.exento_de,
      venta.impuestos_de_pedido ?? [],
    );
    if (conIva !== null) {
      t.columnas("  Con IVA", importe(String(conIva)));
    }
    if (toNumber(l.discount) > 0) {
      t.columnas("  Descuento", `-${importe(l.discount)}`);
    }
    if (l.staff_name) t.linea(`  por ${recortar(l.staff_name, ancho - 6)}`);
  }

  /*
    Aquí iba la leyenda «(E) Exento de IVA». Se quitó: el dueño la encontró
    confusa. La «(E)» junto al nombre y el precio «Con IVA» de los demás ya lo
    dicen.
  */

  t.separador();

  // --- Totales --------------------------------------------------------------
  /*
   * ═════════════════════════════════════════════════════════════════════════
   *  ⚠️ SE ENSEÑA LA CUENTA ENTERA, NO SOLO EL RESULTADO
   * ═════════════════════════════════════════════════════════════════════════
   *
   * Antes se saltaba de los productos al TOTAL con un «Descuentos −1,98»
   * suelto en medio. El cliente que quiere comprobar la suma no puede: le
   * faltan los dos números de los que sale la resta.
   *
   * Ahora sale la cuenta como se hace a mano — subtotal, lo que se resta, lo
   * que se suma, total — y cada línea se explica sola. Es la diferencia entre
   * un papel que se guarda y uno que se tira.
   */
  const hayDescuento = toNumber(venta.discount_total) > 0;
  const hayImpuesto = toNumber(venta.tax_total) > 0;
  const hayRedondeo = toNumber(venta.rounding_adjustment) !== 0;

  /*
   * ⚠️ El subtotal solo sale si hay algo entre él y el total.
   *
   * Sin descuento ni impuesto, «Subtotal» y «TOTAL» son el mismo número dos
   * veces — y una línea que repite la de abajo enseña al ojo a saltarse el
   * bloque justo donde a veces sí hay algo que mirar.
   */
  if (hayDescuento || hayImpuesto || hayRedondeo) {
    t.columnas("Subtotal", importe(venta.subtotal));
  }

  if (hayDescuento) {
    t.columnas("Descuento", `-${importe(venta.discount_total)}`);

    /*
     * ═══════════════════════════════════════════════════════════════════════
     *  ⚠️ Y DE QUÉ CÓDIGO SALIÓ, QUE ES LO QUE FALTABA
     * ═══════════════════════════════════════════════════════════════════════
     *
     * Un «−1,98» a secas no distingue las dos cosas que pueden haberlo puesto
     * ahí: una promoción que decidió el dueño, o alguien que decidió rebajar.
     * Y esa es justo la diferencia que importa cuando el papel vuelve al
     * mostrador con una queja.
     *
     * Va en su PROPIA línea y no pegado a «Descuento» porque un código puede
     * tener 32 caracteres: en la misma línea, `columnas` recortaría la
     * etiqueta y saldría «Descuen…» con el código a medias.
     *
     * Sin código, no se escribe nada. El hueco dice la verdad: lo puso una
     * persona a mano.
     */
    if (venta.discount_code_text) {
      t.linea(`  código ${recortar(venta.discount_code_text, ancho - 10)}`);
    }
  }

  /*
   * ⚠️ Igual que en el recibo de pantalla: el IGTF con su nombre y su tasa, y
   * en «Impuesto» solo lo que queda — el de los productos. Los de pedido ya
   * están dentro de `tax_total`; enumerarlos sin restarlos imprimiría un ticket
   * cuyas líneas no suman su propio total.
   */
  const impuestosDePedido = venta.impuestos_de_pedido ?? [];
  const sumaDePedido = impuestosDePedido.reduce((acc, x) => acc + toNumber(x.amount), 0);
  const impuestoDeProductos = toNumber(venta.tax_total) - sumaDePedido;

  if (impuestoDeProductos > 0) {
    t.columnas("Impuesto", importe(String(impuestoDeProductos)));
  }
  for (const x of impuestosDePedido) {
    /*
      Con algo exento de este impuesto, la base va en su propia línea: en 58 mm
      no cabe «IVA 16% sobre 70,00» al lado del monto. Es como lo pone
      cualquier ticket en Venezuela, y es lo que explica que el IVA no sea el
      16 % del total.
    */
    if (exentosDe.includes(x.name)) {
      t.columnas(
        recortar(`Base ${x.name}`, ancho - 12),
        importe(x.base_amount),
      );
    }
    t.columnas(
      recortar(`${x.name} ${x.rate_percent}%`, ancho - 12),
      importe(x.amount),
    );
  }
  if (hayRedondeo) {
    t.columnas("Redondeo", importe(venta.rounding_adjustment));
  }

  // El TOTAL va a doble tamaño: es lo único que el cliente busca con la vista.
  t.separador("=");
  t.negrita().doble();

  /*
   * ⚠️ La moneda al lado del número si cabe, y en su línea si no.
   *
   * A doble tamaño solo hay la mitad de columnas —dieciséis en 58 mm— y
   * «TOTAL» más «1.234,56 USD» no entran. `columnas` no avisa: recorta la
   * etiqueta por la izquierda y sale «TOTA». Se mide antes.
   */
  const totalConMoneda = monto(convertir(venta.total), codigoDelPapel, mon);
  if ("TOTAL".length + 1 + totalConMoneda.length <= t.anchoLinea) {
    t.columnas("TOTAL", totalConMoneda);
    t.normal();
  } else {
    t.columnas("TOTAL", importe(venta.total));
    t.normal();
    t.derecha().linea(monedaImpresa(codigoDelPapel)).izquierda();
  }
  // En otra moneda, la tasa y lo que equivale en la de los precios.
  if (otra) {
    t.columnas("Tasa", `1 ${monedaImpresa(venta.currency_code)} = ${formatRate(String(otra.tasa))}`);
    t.columnas("Equivale a", monto(venta.total, venta.currency_code, mon));
  }
  t.separador("=");

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ SIN «FORMA DE PAGO» NI «VUELTO» (lo pidió el dueño, 2026-09-29)
    ═══════════════════════════════════════════════════════════════════════════

    Aquí iba el método con que se pagó, en su moneda, con la tasa y el
    «equivale a» —y el vuelto, en su bloque—. El dueño lo vio impreso («Efectivo
    USD · tasa 3.350,00 · 13.400 COP · equivale a 4,00 USD») y pidió quitarlo:
    «fue un error poner eso». El recibo termina en el total; cómo se pagó
    sigue a la vista en la ventana de la venta («Cómo pagó»), fuera del papel.
  */

  // --- Notas y anulación ----------------------------------------------------
  if (venta.note) {
    t.separador();
    t.parrafo(venta.note);
  }

  if (venta.status === "voided") {
    t.separador();
    t.centrar().negrita().doble();
    t.linea("ANULADA");
    t.normal().centrar();
    if (venta.void_reason) t.parrafo(venta.void_reason);
    t.izquierda();
  }

  if (venta.was_offline) {
    t.centrar().linea("(registrada sin conexión)").izquierda();
  }

  // --- Pie ------------------------------------------------------------------
  /*
   * ⚠️ La despedida separada de la marca por un salto.
   *
   * Iban una debajo de otra y se leían como una sola frase: «¡Gracias por su
   * compra! Softfi». La primera es del negocio y la segunda no — pegadas, el
   * agradecimiento parece nuestro y no suyo.
   */
  // Sin nota ni anulación, el total ya cierra con su «====»: otra raya
  // debajo sería una línea de más (desde que no va la forma de pago).
  if (venta.note || venta.status === "voided" || venta.was_offline) t.separador();
  t.salto();
  t.centrar();
  t.negrita().parrafo(negocio?.settings?.receipt_footer || "¡Gracias por su compra!");
  t.negrita(false).salto();
  t.linea("softfi.online");

  // Tres saltos antes de cortar: la cuchilla está unos centímetros por encima
  // del cabezal, y sin este avance el corte se comería las últimas líneas.
  t.avanzar(3);
  if (op.cortarPapel) t.cortar();
  if (op.abrirCajon) t.abrirCajon();
}

/** El recibo en bytes, para la impresora térmica. */
export function construirTicket(
  venta: Sale,
  negocio: Tenant | undefined,
  op: OpcionesTicket,
): Uint8Array {
  const t = new Ticket(op.ancho, op.pagina);
  componerTicket(t, venta, negocio, op);
  return t.bytes();
}

/**
 * El recibo como PDF, del tamaño del rollo.
 *
 * ⚠️ ES EL CAMINO QUE FUNCIONA SIN TOCAR DRIVERS, y por eso existe.
 *
 * El ticket térmico necesita WebUSB, y en Windows el sistema reserva la
 * impresora en cuanto tiene su driver puesto: para usarla desde el navegador
 * habría que reemplazarlo con Zadig, y eso la saca de la lista de impresoras
 * de Windows para todo lo demás.
 *
 * El PDF lleva el tamaño de página dentro, así que se imprime con el driver
 * normal —el que Windows instaló solo— y sale del ancho del rollo. Es lo que
 * hace Fina, y es lo que pidió el dueño: «imprimir sin instalar drivers ni
 * nada».
 */
export function ticketComoPdf(
  venta: Sale,
  negocio: Tenant | undefined,
  op: OpcionesTicket,
): Blob {
  const t = new TicketEnLineas(op.ancho);
  componerTicket(t, venta, negocio, op);
  return reciboComoPdf(t.resultado(), op.ancho, t.columnasDelPapel);
}

// =============================================================================
//  Ticket de prueba
// =============================================================================

/**
 * El ticket que sale al pulsar "Imprimir prueba" en Ajustes.
 *
 * Está pensado para diagnosticar de un vistazo, sin tener que hacer una venta:
 * si los acentos salen mal, se ve en la línea de la ñ; si el ancho está mal
 * configurado, la regla de números se corta o sobra.
 */
/**
 * El ticket de prueba, escrito una sola vez.
 *
 * ⚠️ Mismo motivo que `componerTicket`: la prueba sale por dos caminos —bytes a
 * la térmica, o PDF por el diálogo— y con dos funciones acabarían diciendo
 * cosas distintas. Y una prueba que no se parece al recibo de verdad no prueba
 * nada.
 */
export function componerTicketDePrueba(
  t: ArmadorDeTicket,
  nombreNegocio: string,
  op: OpcionesTicket,
): void {
  // En negrita como el recibo: una prueba que no se le parece no prueba nada.
  t = enNegrita(t);
  const ancho = t.anchoLinea;

  t.iniciar();

  t.centrar().negrita().doble().linea("PRUEBA").normal();
  t.centrar().parrafo(nombreNegocio).salto();

  t.izquierda().separador();

  // La regla: si el último número que se ve no es el ancho configurado, es que
  // el papel es de otro tamaño.
  t.linea(`Ancho: ${op.ancho} mm (${ancho} columnas)`);
  t.linea(regla(ancho));
  t.separador();

  // La prueba de fuego de la tabla de caracteres.
  t.negrita().linea("ACENTOS").negrita(false);
  t.linea("ñÑ áéíóú ÁÉÍÓÚ üÜ ¿? ¡!");
  t.parrafo("Peluquería El Ángel · Café · Champú · Piña · Almuerzo económico");
  t.separador();

  // La prueba del formato de precios.
  t.negrita().linea("ALINEACIÓN").negrita(false);
  t.columnas("Corte de cabello", "10,00");
  t.columnas("Producto con nombre muy largo que no cabe", "125,50");
  t.negrita().doble().columnas("TOTAL", "135,50").normal();
  t.separador();

  t.centrar();
  t.linea("Si lees esto con los acentos");
  t.linea("bien puestos, quedó lista.");
  t.salto();
  t.linea(new Date().toLocaleString("es-VE"));

  t.avanzar(3);
  if (op.cortarPapel) t.cortar();
}

/** El ticket de prueba en bytes, para la impresora térmica. */
export function ticketDePrueba(nombreNegocio: string, op: OpcionesTicket): Uint8Array {
  const t = new Ticket(op.ancho, op.pagina);
  componerTicketDePrueba(t, nombreNegocio, op);
  return t.bytes();
}

/**
 * El ticket de prueba como PDF, igual que un recibo de verdad.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ ESTO TENÍA QUE EXISTIR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La prueba dibujaba su PROPIO ticket en HTML, aparte, y lo imprimía por el
 * camino viejo. Dos problemas:
 *
 *   1. Salía una hoja en blanco. Lo vio el dueño al probarla.
 *   2. Y aunque hubiera salido bien, habría estado probando otra cosa. Era un
 *      segundo diseño de recibo, con su propio ancho y su propia letra, que
 *      podía verse perfecto mientras el recibo de verdad salía cortado —
 *      exactamente el fallo que acabábamos de perseguir tres días.
 *
 * Una prueba que no pasa por el mismo sitio que la venta no prueba nada. Ahora
 * usa el mismo armador, el mismo PDF y el mismo ancho imprimible: si esto sale
 * bien, la venta sale bien.
 */
export function pruebaComoPdf(nombreNegocio: string, op: OpcionesTicket): Blob {
  const t = new TicketEnLineas(op.ancho);
  componerTicketDePrueba(t, nombreNegocio, op);
  return reciboComoPdf(t.resultado(), op.ancho, t.columnasDelPapel);
}

/** "1234567890123..." con marcas cada 10, para medir el ancho real del papel. */
function regla(ancho: number): string {
  let salida = "";
  for (let i = 1; i <= ancho; i++) {
    salida += i % 10 === 0 ? String(Math.floor(i / 10) % 10) : ".";
  }
  return salida;
}
